<script setup>
import AppLayout from "../../../layouts/AppLayout";
import PurchaseRentSearch from "@/views/purchase/_modules/PurchaseRentSearch.vue";
import PurchaseRentCountry from "@/views/purchase/_modules/PurchaseRentCountry";
import PurchaseRentPlan from "@/views/purchase/_modules/PurchaseRentPlan.vue";
import PurchaseRentShipment from "@/views/purchase/_modules/PurchaseRentShipment.vue";
import PurchaseRentDiscount from "@/views/purchase/_modules/PurchaseRentDiscount.vue";
import PurchaseCard from "@/views/purchase/_modules/PurchaseCard.vue";
import PurchaseRentDevice from "../_modules/PurchaseRentDevice.vue";

import { useDestinationStore } from "../../../store/destinationStore";
import { useOrderStore } from "../../../store/orderStore";
import { useShipmentStore } from "../../../store/shipmentStore";
import { usePeriodStore } from "../../../store/periodStore";
import { useDeviceStore } from "../../../store/deviceStore";
import { usePlanStore } from "../../../store/planStore";

import { computed, onMounted } from "vue";
import { useRouter } from 'vue-router';
import { useI18n } from "vue-i18n/dist/vue-i18n";
import { ref } from "vue";
import ShipmentSkeletonLoader from "../_modules/purchase-rent-plan/Shipment/ShipmentSkeletonLoader.vue";

const router = useRouter();

const destinationStore = useDestinationStore();
const orderStore = useOrderStore();
const shipmentStore = useShipmentStore();
const periodStore = usePeriodStore();
const deviceStore = useDeviceStore();
const planStore = usePlanStore();

const { t } = useI18n({
    useScope: 'global'
});

const loading = ref(true);

const validationMessage = computed(() => {
    let message = "";
    if (destinationStore.validationMessage) {
        message += destinationStore.validationMessage;
    }
    if (periodStore.validationMessage) {
        message += periodStore.validationMessage;
    }
    if (planStore.validationMessage) {
        message += planStore.validationMessage;
    }
    if (shipmentStore.validationMessage) {
        message += shipmentStore.validationMessage;
    }
    return message;
});

onMounted(async () => {
    await init();
});

async function init() {
    loading.value = true;
    await deviceStore.fetchDevices();
    await shipmentStore.fetchPickupLocations();
    await shipmentStore.fetchDeliveryTypes();
    loading.value = false;
}

function purchase() {
    if (!validationMessage.value) {
        router.push({name: 'PurchaseCheckoutIndex'});
    }
}
</script>

<template>
    <app-layout>
        <div class="purchase-rent-index">
            <div class="container mx-auto">
                <!-- Global Destination Search -->
                <purchase-rent-search />

                <!-- Popular Destinations -->
                <purchase-rent-country />

                <div class="purchase-rent-index__body">
                    <div class="purchase-rent-index__body--left">
                        <div class="mb-8">
                            <purchase-rent-plan/>
                        </div>
                        <div class="mb-8">
                            <purchase-rent-device />
                        </div>
                        <div class="mb-8">
                            <template v-if="loading">
                                <ShipmentSkeletonLoader />
                            </template>

                            <template v-if="!loading">
                                <purchase-rent-shipment />
                            </template>
                        </div>

                        <purchase-rent-discount />
                    </div>
                    <div class="purchase-rent-index__body--right">
                        <div class="help-text">
                            Need a custom plan specifically for your trip? <br>
                            Click <a href="https://skygowifi.com/customersupport">here</a> to contact us.
                        </div>

                        <div class="flex justify-end sticky top-2">
                            <div class="purchase-rent-index__card">
                                <purchase-card />

                                <div
                                    v-if="validationMessage"
                                    class="purchase-rent-index__card-validate-message"
                                    v-html="validationMessage"
                                ></div>

                                <div class="purchase-rent-index__card-buttons">

                                    <div class="purchase-rent-index__purchase-button">
                                        <button
                                            @click="purchase"
                                        >Purchase</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<style lang="scss">
.purchase-rent-index {
    &__body {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;

        &--left {
            width: 100%;
            max-width: 50%;
        }

        &--right {
            width: 100%;
            max-width: 35%;

            .help-text {
                font-weight: 500;
                font-size: 1rem;
                text-align: right;
                margin-top: 6rem;
                margin-bottom: 2rem;
                width: 100%;

                a {
                    color: #7DD3FC;
                }
            }
        }
    }

    &__card {
        width: 100%;

        .purchase-card {
            margin-bottom: 1.5rem;
        }

        &-buttons {
            display: flex;
            justify-content: end;
            flex-wrap: wrap;
        }

        &-validate-message {
            border: 2px solid #7DD3FC;
            border-radius: 8px;
            background: #ffffff;
            padding: 1rem 1.5rem;
            margin-bottom: 1.5rem;
            color: #CA0000;
        }
    }

    &__purchase-button {
        width: 100%;

        button {
            font-size: 24px;
            color: #ffffff;
            border-radius: 8px;
            width: 100%;
            height: 60px;
            background: #84CC16;
        }
    }

    &__add-another-button {
        width: 100%;
        margin-bottom: 1rem;

        button {
            font-size: 24px;
            color: #7DD3FC;
            border-radius: 8px;
            width: 100%;
            height: 60px;
            background: #ffffff;
            border: 2px solid #7DD3FC;

            &:hover {
                background: #7DD3FC;
                color: #ffffff;
            }
        }
    }
}

.calendar {
    position: absolute !important;
    z-index: 10;
    top: 50px;
    left: 25px;
}


@media (max-width: 1100px) {
    .purchase-rent-index {

        &__body {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;

            &--left {
                width: 100%;
                max-width: 100%;
            }

            &--right {
                width: 100%;
                max-width: 100%;
            }
        }
    }
}
</style>